import * as Sentry from "@sentry/react";
import { type JSX, type ReactNode, useState } from "react";
import ReactCountryFlag from "react-country-flag";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import { ProxyLocationTag } from "common/components/Proxy/index";
import RenderIf from "common/components/RenderIf";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import SelectButton from "common/components/ui/SelectButton";
import { Tag } from "common/components/ui/Tag";
import useProxies from "common/datahooks/useProxies";
import useWorkspaceProxies from "common/datahooks/useWorkspaceProxies";

interface ProxySelectProps {
  // selected proxy is either a country code or custom proxy ID
  selectedProxy: string;
  setSelectedProxy: (selectedProxy: string) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}

export default function ProxySelect({
  selectedProxy,
  setSelectedProxy,
  isLoading = false,
  isDisabled = false,
}: ProxySelectProps) {
  const { workspaceProxies, isLoadingWorkspaceProxies } = useWorkspaceProxies();
  const { proxies, isLoadingProxies } = useProxies();

  const isFinalLoading =
    isLoadingProxies || isLoadingWorkspaceProxies || isLoading;

  let selectValue: ReactNode;
  let rightIcon: JSX.Element;
  let isValidProxy = true;
  if (isFinalLoading) {
    selectValue = "Loading countries...";
  } else if (selectedProxy) {
    let location: { countryCode: string; name: string };
    let name: string;

    if (selectedProxy.length === 2) {
      const proxy = proxies.find(
        ({ countryCode }) => countryCode === selectedProxy,
      );
      if (proxy) {
        name = `AF - ${proxy.country}`;
        location = { countryCode: proxy.countryCode, name: proxy.country };
      } else {
        name = "Deleted proxy";
        isValidProxy = false;
      }
    } else {
      const proxy = workspaceProxies.find(({ id }) => id === selectedProxy);

      if (proxy) {
        name = proxy.name;
        isValidProxy = proxy.valid;

        // Only valid proxies will have location
        if (isValidProxy) {
          location = {
            countryCode: proxy.location.countryISO,
            name: proxy.location.city || proxy.location.country,
          };
        }
      } else {
        Sentry.captureMessage("Custom Proxy not found");
        name = "Deleted proxy";
        isValidProxy = false;
      }
    }

    selectValue = name;
    rightIcon = (
      <ProxyLocationTag location={location} isValid={isValidProxy} size="md" />
    );
  } else {
    selectValue = <span className="text-black-500">Select location</span>;
  }

  const [inputValue, setInputValue] = useState("");

  const lowercasedInput = inputValue.toLowerCase();

  const filteredProxies = proxies
    ? proxies.filter(({ country }) =>
        country.toLocaleLowerCase().includes(lowercasedInput),
      )
    : [];
  const filteredWorkspaceProxies = workspaceProxies
    ? workspaceProxies.filter(({ name }) =>
        name.toLocaleLowerCase().includes(lowercasedInput),
      )
    : [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={isDisabled || isFinalLoading}>
        <SelectButton
          rightIcon={rightIcon}
          hasSelection={!!selectedProxy}
          hasError={!isValidProxy}
          isLoading={isFinalLoading}
        >
          {selectValue}
        </SelectButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[var(--radix-popper-anchor-width)]">
        <MenuSearchInput
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
        <DropdownMenuList>
          {filteredProxies.length || filteredWorkspaceProxies.length ? (
            <div className="max-h-40 overflow-y-auto scrollbar-thin lg:max-h-56">
              <RenderIf condition={!!workspaceProxies?.length}>
                <DropdownMenuLabel>Custom proxies</DropdownMenuLabel>
                {filteredWorkspaceProxies.map(
                  ({ id, name, location, valid }) => (
                    <DropdownMenuItem
                      key={id}
                      onSelect={() => {
                        setSelectedProxy(id);
                      }}
                      disabled={!valid}
                    >
                      <span className="flex-1 truncate text-left">{name}</span>
                      <ProxyLocationTag
                        location={
                          location && {
                            countryCode: location.countryISO,
                            name: location.city || location.country,
                          }
                        }
                        size="md"
                        isValid={valid}
                      />
                    </DropdownMenuItem>
                  ),
                )}
              </RenderIf>
              <DropdownMenuLabel>Global proxies</DropdownMenuLabel>
              {filteredProxies.map(({ countryCode, country }) => (
                <DropdownMenuItem
                  key={countryCode}
                  onSelect={() => {
                    setSelectedProxy(countryCode);
                  }}
                >
                  <span className="truncate text-left">AF - {country}</span>
                  <Tag
                    variant="tertiary"
                    className="ml-auto"
                    leftIcon={<ReactCountryFlag countryCode={countryCode} />}
                  >
                    <span>{country}</span>
                  </Tag>
                </DropdownMenuItem>
              ))}
            </div>
          ) : (
            <p className="mx-4 break-words py-4 text-center text-black-500">
              No results for{" "}
              <span className="text-black-950">{inputValue}</span>
            </p>
          )}
        </DropdownMenuList>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
